import notesBg from 'assets/icons/edges.svg';
import submitIcon from 'assets/icons/submitIcon.svg';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import { Dispatch, SetStateAction, useState } from 'react';
import { getBackgroundColor } from 'utils/avatarUtilities';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';
import { getInitials } from 'utils/getInitials';
import NotesLoadingSkeleton from './components/NotesSkeleton/index';
import classes from './NotesSection.module.scss';

const NotesSection = ({
  ndc,
  visibility,
  handleCancel,
  isNotesLoading,
  notesData,
  isNoteSaving,
  handleAddNote,
  setNotesData,
  isNoteEdited,
  setIsNoteEdited: _setIsNoteEdited,
  isNoteDeleted,
  setIsNoteDeleted: _setIsNoteDeleted,
  handleEditNote,
  handleDeleteNote,
  deletingNote,
  isNoteDeleting,
  isDeleteClicked,
  setIsDeleteClicked,
  setDeletingNote,
}: {
  ndc: string;
  visibility: boolean;
  handleCancel: () => void;
  isNotesLoading: boolean;
  notesData: notesInterface[] | undefined;
  isNoteSaving: boolean;
  handleAddNote: (ndc: string, value: string) => Promise<boolean | undefined>;
  setNotesData: Dispatch<SetStateAction<notesInterface[] | undefined>>;
  isNoteEdited: Map<number, boolean>;
  setIsNoteEdited: Dispatch<SetStateAction<Map<number, boolean>>>;
  isNoteDeleted: Map<number, boolean>;
  setIsNoteDeleted: Dispatch<SetStateAction<Map<number, boolean>>>;
  handleEditNote: (id: number, note: string) => Promise<void>;
  handleDeleteNote: (id: number) => Promise<void>;
  deletingNote: notesInterface | null;
  isNoteDeleting: boolean;
  isDeleteClicked: boolean;
  setIsDeleteClicked: Dispatch<SetStateAction<boolean>>;
  setDeletingNote: Dispatch<SetStateAction<notesInterface | null>>;
}) => {
  const [value, setValue] = useState<string>('');
  const [toggleInput, setToggleInput] = useState<Set<number>>(new Set());

  const handleToggle = (id: number) => {
    setToggleInput(prev => {
      const newSet = new Set(prev); // Create a new Set instance
      if (newSet.has(id)) {
        newSet.delete(id); // Remove the ID if it exists
      } else {
        newSet.add(id); // Add the ID if it doesn't exist
      }
      return newSet; // Return the new Set
    });
  };

  return (
    <Dialog
      blockScroll
      resizable={false}
      draggable={false}
      headerClassName={classes.modalHeader}
      header={`Notes`}
      visible={visibility}
      style={{ width: '30vw', right: '-28%', height: '37rem', top: '9rem' }}
      onHide={() => {
        if (!visibility) return;
        handleCancel();
      }}
    >
      <>
        {isNotesLoading ? (
          <NotesLoadingSkeleton />
        ) : (
          <div className={classes.notesContainer}>
            <>
              <InputTextarea
                autoResize
                value={value}
                placeholder="Note..."
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
                rows={5}
                cols={40}
                disabled={isNoteSaving}
                style={{ width: '94%', margin: '1rem' }}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '0.5rem',
                  marginLeft: '0.5rem',
                }}
              >
                <Button
                  className={classes.noteSubmitBtn}
                  outlined
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    color: '#028383',
                  }}
                  onClick={async () => {
                    const res = await handleAddNote(ndc, value);
                    if (res) {
                      setValue('');
                    }
                  }}
                  loading={isNoteSaving}
                  disabled={!value}
                >
                  Submit <img src={submitIcon} alt="icon" />
                </Button>

                <Button
                  className={classes.noteSubmitBtn}
                  outlined
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    color: 'red',
                  }}
                  onClick={handleCancel}
                  disabled={isNoteSaving}
                >
                  Cancel
                </Button>
              </div>

              <div className={classes.prevNotesContainer}>
                {notesData && notesData.length ? (
                  <>
                    {notesData.map((item, index) => {
                      return (
                        <div key={index}>
                          <div className={classes.noteSection}>
                            <span style={{ marginBottom: '0px', cursor: 'pointer' }}>
                              <Tooltip
                                target={`#profileAvatar-${index}`}
                                content={item.CreatedBy}
                                mouseTrack
                                mouseTrackLeft={10}
                                position="bottom"
                              />
                              <Avatar
                                title={item.CreatedBy}
                                id={`profileAvatar-${index}`}
                                shape="circle"
                                label={getInitials(item.CreatedBy)}
                                style={{
                                  backgroundColor: getBackgroundColor(item.CreatedBy[0]),
                                  color: '#522D6E',
                                  marginRight: '0.5rem',
                                }}
                              />
                            </span>
                            <div className={classes.noteContainer}>
                              <div className={classes.notesSvg}>
                                <img src={notesBg} alt="notesicon" />
                              </div>
                              <div className={classes.noteElement}>
                                <div className={classes.noteBox}>
                                  {toggleInput.has(item.ID) ? (
                                    <InputTextarea
                                      value={item.Notes}
                                      cols={30}
                                      rows={3}
                                      onChange={e =>
                                        setNotesData(() => {
                                          const allNotes = deepCopy(notesData);
                                          allNotes.filter(note => note.ID === item.ID)[0].Notes =
                                            e.target.value;
                                          return allNotes;
                                        })
                                      }
                                    />
                                  ) : (
                                    <p>{item.Notes}</p>
                                  )}
                                  {/* {item.Notes} */}
                                </div>
                                <div className={classes.noteBtnSection}>
                                  <Button
                                    icon="pi pi-trash"
                                    className={classes.btn}
                                    outlined
                                    style={{ color: 'red' }}
                                    loading={isNoteDeleted && isNoteDeleted.get(item.ID)}
                                    onClick={() => {
                                      setIsDeleteClicked(true);
                                      setDeletingNote(item);
                                    }}
                                  />
                                  {!toggleInput.has(item.ID) ? (
                                    <Button
                                      icon="pi pi-pencil"
                                      className={classes.btn}
                                      outlined
                                      style={{ color: '#5A5959' }}
                                      loading={isNoteEdited && isNoteEdited.get(item.ID)}
                                      onClick={() => handleToggle(item.ID)}
                                    />
                                  ) : (
                                    <Button
                                      icon="pi pi-check"
                                      className={classes.btn}
                                      outlined
                                      style={{ color: '#028383' }}
                                      onClick={async () => {
                                        handleEditNote(item.ID, item.Notes);
                                        handleToggle(item.ID);
                                      }}
                                    />
                                  )}
                                </div>
                                <span className={classes.notedateContainer}>
                                  {isNoteEdited && isNoteEdited.get(item.ID) ? (
                                    <Skeleton height="1rem" width="10rem" />
                                  ) : (
                                    convertIsoToDateString(item.CreatedDate)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div>No Note is added yet</div>
                )}
              </div>
            </>
          </div>
        )}
      </>

      <ConfirmationModal
        visibility={isDeleteClicked}
        modalHeading="Confirmation"
        modalContent={'Are you sure want to delete the note?'}
        loading={isNoteDeleting}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        handleCancel={() => {
          setIsDeleteClicked(false);
          setDeletingNote(null);
        }}
        handleConfirm={async () => {
          if (deletingNote) {
            handleDeleteNote(deletingNote?.ID);
          }
        }}
      />
    </Dialog>
  );
};

export default NotesSection;
